'use client';

import { isValidEmail } from '@pickleballinc/lib/email-utils';
import { ActionResponse } from '@pickleballinc/lib/server/next/actions/types';
import { useCallback, useState } from 'react';

import {
  SubscribeParams,
  useNewsletterSubscribe,
} from '@/hooks/newsletter/useSubscribeToNewsletter';
import { CaptchaAction } from '@/utils/helpers/captcha-actions';
import { getErrorMessage } from '@/utils/helpers/newsletter';

export const useNewsletterSubscribeForm = (
  action: (params: SubscribeParams) => Promise<ActionResponse<unknown>>,
  captchaAction: CaptchaAction
) => {
  const [email, setEmail] = useState('');
  const [localError, setLocalError] = useState('');

  const {
    mutate: subscribe,
    error,
    reset,
    isPending,
    isSuccess,
    isError,
  } = useNewsletterSubscribe(action, captchaAction);

  const handleSubscribe = useCallback(() => {
    const normalizedEmail = email.trim();

    if (!isValidEmail(normalizedEmail)) {
      setLocalError('Please enter a valid email!');
      return;
    }

    setLocalError('');
    reset();
    subscribe(normalizedEmail);
  }, [email, subscribe, reset]);

  let displayError = '';

  if (localError) {
    displayError = localError;
  } else if (isError) {
    displayError = getErrorMessage(error);
  }

  const handleChange = (value: string) => {
    setEmail(value);
    setLocalError('');
    if (isError || isSuccess) reset();
  };

  return {
    email,
    onEmailChange: handleChange,
    handleSubscribe,
    displayError,
    isPending,
    isSuccess,
  };
};
